<script>
export default {
  name: "AutoComplete",

  model: {
    prop: "value",
    event: "input"
  },

  props: {
    value: {
      type: String,
      default: ""
    },

    /*
      Exemplo:

      triggers: [
        {
          char: "/",
          options: [...],
          matchKey: "value",
          placeholder: "Digite uma função..."
        },
        {
          char: "data.",
          options: [...],
          matchKey: "value",
          placeholder: "Digite uma variável..."
        }
      ]
    */
    triggers: {
      type: Array,
      default: () => []
    },

    showTriggerHint: {
      type: Boolean,
      default: true
    },

    noMatchText: {
      type: String,
      default: "Nenhum resultado encontrado"
    },

    placeholder: {
      type: String,
      default: ""
    },

    rows: {
      type: Number,
      default: 1
    },

    maxItems: {
      type: Number,
      default: 10
    },

    noResultsText: {
      type: String,
      default: "Nenhum resultado encontrado"
    }
  },

  data() {
    return {
      innerValue: this.value,

      selectedIndex: 0,

      filteredOptions: [],

      showDropdown: false,

      activeQuery: "",

      currentTrigger: null,

      currentTriggerConfig: null,

      queryStartIndex: 0,

      cursorPosition: 0,

      isSelecting: false
    };
  },

  computed: {
    displayOptions() {
      return this.filteredOptions.slice(0, this.maxItems);
    },

    triggerChars() {
      return [
        ...new Set(
          this.triggers
            .map((trigger) => trigger && trigger.char)
            .filter((char) => typeof char === "string" && char.length > 0)
        )
      ];
    },

    currentPlaceholder() {
      return this.currentTriggerConfig?.placeholder || this.placeholder;
    },

    currentMatchKey() {
      return this.currentTriggerConfig?.matchKey || "label";
    },

    isTextarea() {
      return this.rows > 1;
    },

    currentLabelKey() {
      return this.currentTriggerConfig?.labelKey || "label";
    },

    currentInsertKey() {
      return this.currentTriggerConfig?.insertKey || "insert";
    }
  },

  watch: {
    value(newValue) {
      if (newValue !== this.innerValue) {
        this.innerValue = newValue;
      }
    },

    triggers: {
      handler() {
        this.updateFilter(this.activeQuery);
      },
      deep: true
    }
  },

  methods: {
    getOptionMatchValue(item) {
      if (typeof item === "string") {
        return item;
      }

      return (
        item[this.currentMatchKey] || item.value || item.label || item.key || ""
      );
    },

    getOptionDisplayLabel(item) {
      if (typeof item === "string") {
        return item;
      }

      return (
        item[this.currentLabelKey] || item.label || item.key || item.value || ""
      );
    },

    getOptionInsertValue(item) {
      if (typeof item === "string") {
        return item;
      }

      return (
        item[this.currentInsertKey] ||
        item.insert ||
        item.value ||
        item.label ||
        item.key ||
        ""
      );
    },

    onInput(event) {
      const value = event.target.value;

      const cursor = event.target.selectionStart || 0;

      this.cursorPosition = cursor;

      this.innerValue = value;

      this.$emit("input", value);

      const parsed = this.extractQuery(value.slice(0, cursor));

      if (parsed) {
        this.activeQuery = parsed.query;

        this.currentTrigger = parsed.triggerChar;

        this.currentTriggerConfig = parsed.triggerConfig;

        this.queryStartIndex = parsed.startIndex;

        this.updateFilter(parsed.query);

        this.showDropdown = true;
      } else {
        this.closeDropdown();
      }
    },

    extractQuery(text) {
      let bestMatch = null;

      this.triggers.forEach((trigger) => {
        const char = trigger.char;

        if (!char) {
          return;
        }

        let index = text.lastIndexOf(char);

        while (index >= 0) {
          const before = index === 0 || /\s/.test(text[index - 1]);

          const tail = text.slice(index + char.length);

          const hasSpace = /\s/.test(tail);

          if (before && !hasSpace) {
            if (!bestMatch || index > bestMatch.startIndex) {
              bestMatch = {
                triggerChar: char,
                query: tail,
                startIndex: index,
                triggerConfig: trigger
              };
            }

            break;
          }

          index = text.lastIndexOf(char, index - 1);
        }
      });

      return bestMatch;
    },

    updateFilter(query) {
      const lowerQuery = (query || "").toLowerCase();

      const source = this.currentTriggerConfig?.options || [];

      if (!lowerQuery) {
        this.filteredOptions = [...source];

        this.selectedIndex = 0;

        return;
      }

      this.filteredOptions = source.filter((item) => {
        const label = this.getOptionMatchValue(item).toLowerCase();

        return label.includes(lowerQuery);
      });

      this.selectedIndex = 0;
    },

    handleKeyDown(event) {
      if (!this.showDropdown) {
        return;
      }

      if (!this.displayOptions.length) {
        return;
      }

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();

          this.selectedIndex = Math.max(0, this.selectedIndex - 1);

          this.scrollToSelected();

          break;

        case "ArrowDown":
          event.preventDefault();

          this.selectedIndex = Math.min(
            this.displayOptions.length - 1,
            this.selectedIndex + 1
          );

          this.scrollToSelected();

          break;

        case "Enter":
        case "Tab":
          event.preventDefault();

          this.selectItem(this.selectedIndex);

          break;

        case "Escape":
          this.closeDropdown();

          break;
      }
    },

    scrollToSelected() {
      this.$nextTick(() => {
        const container = this.$refs.dropdownContainer;

        const item =
          container &&
          container.querySelector(`#autocomplete-item-${this.selectedIndex}`);

        if (!container || !item) {
          return;
        }

        const itemTop = item.offsetTop;

        const itemBottom = itemTop + item.offsetHeight;

        const containerTop = container.scrollTop;

        const containerBottom = containerTop + container.offsetHeight;

        if (itemTop < containerTop) {
          container.scrollTop = itemTop;
        } else if (itemBottom > containerBottom) {
          container.scrollTop = itemBottom - container.offsetHeight;
        }
      });
    },

    selectItem(index) {
      const selected = this.displayOptions[index];

      if (!selected) {
        return;
      }

      this.isSelecting = true;

      const insertText = this.getOptionInsertValue(selected);

      /*
        remove o trigger:
        /va -> validarCPF('cpf')
      */
      const before = this.innerValue.slice(0, this.queryStartIndex);

      const after = this.innerValue.slice(this.cursorPosition);

      const newValue = before + insertText + after;

      this.innerValue = newValue;

      this.$emit("input", newValue);

      this.$emit("select", {
        item: selected,
        trigger: this.currentTrigger,
        triggerConfig: this.currentTriggerConfig
      });

      this.closeDropdown();

      this.$nextTick(() => {
        const input = this.$refs.textInput;

        if (input) {
          const position = before.length + insertText.length;

          input.focus();

          input.setSelectionRange(position, position);
        }

        this.isSelecting = false;
      });
    },

    onFocus() {
      if (this.activeQuery !== "" && this.filteredOptions.length > 0) {
        this.showDropdown = true;
      }
    },

    onBlur() {
      requestAnimationFrame(() => {
        if (!this.isSelecting) {
          this.closeDropdown();
        }
      });
    },

    closeDropdown() {
      this.showDropdown = false;

      this.currentTrigger = null;

      this.currentTriggerConfig = null;

      this.activeQuery = "";

      this.selectedIndex = 0;
    }
  }
};
</script>

<template>
  <div class="autocomplete relative w-full">
    <!-- TEXTAREA -->
    <textarea
      v-if="isTextarea"
      ref="textInput"
      class="autocomplete-input"
      :rows="rows"
      :placeholder="currentPlaceholder"
      :value="innerValue"
      autocomplete="off"
      @input="onInput"
      @keydown="handleKeyDown"
      @focus="onFocus"
      @blur="onBlur"
    />

    <!-- INPUT -->
    <input
      v-else
      ref="textInput"
      type="text"
      class="autocomplete-input"
      :placeholder="currentPlaceholder"
      :value="innerValue"
      autocomplete="off"
      @input="onInput"
      @keydown="handleKeyDown"
      @focus="onFocus"
      @blur="onBlur"
    />

    <div
      v-if="showTriggerHint && triggerChars.length"
      class="autocomplete-trigger-hint"
    >
      <span>Atalhos disponíveis: digite</span>
      <template v-for="(char, index) in triggerChars">
        <span v-if="index > 0" :key="`separator-${char}`">
          {{ index === triggerChars.length - 1 ? " ou " : ", " }}
        </span>
        <kbd :key="`trigger-${char}`" class="autocomplete-trigger-key">
          {{ char }}
        </kbd>
      </template>
      <span> para ver as sugestões.</span>
    </div>

    <div
      v-if="showDropdown"
      ref="dropdownContainer"
      class="autocomplete-dropdown"
    >
      <ul class="autocomplete-list">
        <li
          v-if="showDropdown && activeQuery && displayOptions.length === 0"
          class="autocomplete-empty"
        >
          <slot
            name="no-results"
            :query="activeQuery"
            :trigger="currentTrigger"
          >
            <b>{{ noMatchText }}</b>
          </slot>
        </li>

        <li
          v-for="(item, index) in displayOptions"
          :key="`${getOptionDisplayLabel(item)}-${index}`"
          :id="`autocomplete-item-${index}`"
          class="autocomplete-item-wrapper"
          :class="{
            striped: index % 2 !== 0
          }"
        >
          <button
            type="button"
            class="autocomplete-item"
            :class="{
              selected: index === selectedIndex
            }"
            @mousedown.prevent="selectItem(index)"
            @mouseover="selectedIndex = index"
          >
            <slot
              name="item"
              :item="item"
              :index="index"
              :selected="index === selectedIndex"
            >
              <div
                class="autocomplete-content"
                :title="
                  typeof item === 'object'
                    ? item.description || getOptionDisplayLabel(item)
                    : getOptionDisplayLabel(item)
                "
              >
                <div
                  class="autocomplete-title"
                  :title="getOptionDisplayLabel(item)"
                >
                  {{ getOptionDisplayLabel(item) }}
                </div>

                <div
                  v-if="typeof item === 'object' && item.description"
                  class="autocomplete-description"
                  :title="item.description"
                >
                  {{ item.description }}
                </div>
              </div>
            </slot>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.autocomplete-item-wrapper.striped {
  background: #fafafa;
}

.autocomplete-item-wrapper.striped .autocomplete-item {
  background: transparent;
}

.autocomplete-title,
.autocomplete-description {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: block;
}

.autocomplete-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.autocomplete-description {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

.autocomplete {
  position: relative;
  width: 100%;
}

.autocomplete-input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  resize: vertical;
  line-height: 1.5;
}

.autocomplete-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.autocomplete-trigger-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 5px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}

.autocomplete-trigger-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  margin-left: 4px;
  padding: 1px 5px;
  border: 1px solid #d1d5db;
  border-bottom-width: 2px;
  border-radius: 4px;
  background: #f9fafb;
  color: #374151;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.autocomplete-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  max-height: 280px;
}

.autocomplete-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.autocomplete-item-wrapper {
  margin: 0;
}

.autocomplete-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.autocomplete-item:hover {
  background: #f3f4f6;
}

.autocomplete-item.selected {
  background: #e5e7eb;
}

.autocomplete-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.autocomplete-empty {
  padding: 12px;
  font-size: 13px;
  color: #6b7280;
}
</style>
